// apps/web/src/features/dashboards/china-requests/ManagersTable.tsx (#1589)
// Per-manager breakdown table. Client-side sortable: clicking a column
// header toggles asc/desc on that field. Read-only — no row actions.
import { useMemo, useState } from 'react';
import type { ChinaRequestsDashboardSummary } from '@magimp/shared';
import { formatNumber, formatVolume, formatWeight } from '../../../i18n/formatters.js';
import { SECTION_CARD, type AppLocale, type DashboardT } from './section.js';

type ManagerRow = ChinaRequestsDashboardSummary['managers'][number];
type SortKey =
  | 'managerName'
  | 'created'
  | 'inWorkNow'
  | 'success'
  | 'refused'
  | 'conversionPct'
  | 'volumeM3';
type SortDir = 'asc' | 'desc';

interface ManagersTableProps {
  managers: readonly ManagerRow[];
  locale: AppLocale;
  t: DashboardT;
}

const COLUMNS = [
  { key: 'managerName', labelKey: 'dashboards:chinaRequests.managers.name', numeric: false },
  { key: 'created', labelKey: 'dashboards:chinaRequests.managers.created', numeric: true },
  { key: 'inWorkNow', labelKey: 'dashboards:chinaRequests.managers.inWorkNow', numeric: true },
  { key: 'success', labelKey: 'dashboards:chinaRequests.managers.success', numeric: true },
  { key: 'refused', labelKey: 'dashboards:chinaRequests.managers.refused', numeric: true },
  { key: 'conversionPct', labelKey: 'dashboards:chinaRequests.managers.conversion', numeric: true },
  // WHY: volume column sorts by volumeM3; weightKg rides along in the cell display but m³ is the sort metric.
  { key: 'volumeM3', labelKey: 'dashboards:chinaRequests.managers.volume', numeric: true },
] as const satisfies ReadonlyArray<{ key: SortKey; labelKey: string; numeric: boolean }>;

// WHY: null conversion sinks to the bottom regardless of direction so an
// unrankable manager never out-sorts a real percentage.
function compare(a: ManagerRow, b: ManagerRow, key: SortKey, dir: SortDir): number {
  const factor = dir === 'asc' ? 1 : -1;
  if (key === 'managerName') return a.managerName.localeCompare(b.managerName) * factor;
  const av = a[key];
  const bv = b[key];
  if (av === null && bv === null) return 0;
  if (av === null) return 1;
  if (bv === null) return -1;
  return (av - bv) * factor;
}

export function ManagersTable({ managers, locale, t }: ManagersTableProps) {
  const [sortKey, setSortKey] = useState<SortKey>('created');
  const [sortDir, setSortDir] = useState<SortDir>('desc');

  const rows = useMemo(
    () => [...managers].sort((a, b) => compare(a, b, sortKey, sortDir)),
    [managers, sortKey, sortDir],
  );

  function toggleSort(key: SortKey): void {
    if (key === sortKey) {
      setSortDir((d) => (d === 'asc' ? 'desc' : 'asc'));
    } else {
      setSortKey(key);
      setSortDir('desc');
    }
  }

  if (managers.length === 0) return null;

  return (
    <section style={{ ...SECTION_CARD, padding: 0, overflowX: 'auto' }}>
      <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: 13 }}>
        <thead>
          <tr style={{ textAlign: 'left', color: 'var(--fg-2)' }}>
            {COLUMNS.map((col) => {
              const active = col.key === sortKey;
              const indicator = active ? (sortDir === 'asc' ? ' ▲' : ' ▼') : '';
              return (
                <th
                  key={col.key}
                  onClick={() => toggleSort(col.key)}
                  style={{
                    padding: 8,
                    textAlign: col.numeric ? 'right' : 'left',
                    cursor: 'pointer',
                    userSelect: 'none',
                    color: active ? 'var(--fg-1)' : 'var(--fg-2)',
                  }}
                >
                  {t(col.labelKey)}
                  {indicator}
                </th>
              );
            })}
          </tr>
        </thead>
        <tbody>
          {rows.map((row) => (
            <tr key={row.managerId} style={{ borderTop: '1px solid var(--border-1)' }}>
              <td style={{ padding: 8 }}>{row.managerName}</td>
              <td style={{ padding: 8, textAlign: 'right' }}>
                {formatNumber(row.created, locale)}
              </td>
              <td style={{ padding: 8, textAlign: 'right' }}>
                {formatNumber(row.inWorkNow, locale)}
              </td>
              <td style={{ padding: 8, textAlign: 'right' }}>
                {formatNumber(row.success, locale)}
              </td>
              <td style={{ padding: 8, textAlign: 'right' }}>
                {formatNumber(row.refused, locale)}
              </td>
              <td style={{ padding: 8, textAlign: 'right' }}>
                {row.conversionPct !== null ? `${formatNumber(row.conversionPct, locale)}%` : '—'}
              </td>
              <td style={{ padding: 8, textAlign: 'right' }}>
                {formatWeight(row.weightKg, locale)} · {formatVolume(row.volumeM3, locale)}
              </td>
            </tr>
          ))}
        </tbody>
      </table>
    </section>
  );
}
